import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, Home, MessageCircle, Award, Activity } from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [dreamScores, setDreamScores] = useState(null);
  const [conciergeQuality, setConciergeQuality] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, onboardingRes, scoresRes, conciergeRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`),
        axios.get(`${API}/admin/analytics/onboarding-stats`),
        axios.get(`${API}/admin/analytics/dream-scores`),
        axios.get(`${API}/admin/analytics/concierge-quality`)
      ]);
      
      setOverview(overviewRes.data);
      setOnboardingStats(onboardingRes.data);
      setDreamScores(scoresRes.data);
      setConciergeQuality(conciergeRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading analytics...</div>
      </div>
    );
  }

  const COLORS = ['#D4AF37', '#C5A059', '#F1D693', '#10B981'];

  const scoreDistributionData = dreamScores?.distribution ? [
    { name: '0-25', value: dreamScores.distribution['0-25'] },
    { name: '25-50', value: dreamScores.distribution['25-50'] },
    { name: '50-75', value: dreamScores.distribution['50-75'] },
    { name: '75-100', value: dreamScores.distribution['75-100'] }
  ] : [];

  const onboardingFunnelData = onboardingStats ? [
    { name: 'Signups', value: onboardingStats.total_signups },
    { name: 'Started', value: onboardingStats.started_onboarding },
    { name: 'Step 1', value: onboardingStats.step1_completed },
    { name: 'Step 2', value: onboardingStats.step2_completed },
    { name: 'Step 3', value: onboardingStats.step3_completed },
    { name: 'Complete', value: onboardingStats.fully_completed }
  ] : [];

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav activePage="/analytics" isAdmin={true} />

      <div className="max-w-[1440px] mx-auto px-8 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-playfair text-5xl font-semibold mb-4">Analytics Dashboard</h1>
          <p className="text-text-secondary text-lg">Monitor user engagement and platform performance</p>
        </motion.div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
            <Users className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{overview?.total_users || 0}</div>
            <div className="text-text-secondary">Total Users</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-6">
            <Award className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{overview?.onboarding_completion_rate || 0}%</div>
            <div className="text-text-secondary">Onboarding Completion</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-6">
            <Home className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{overview?.total_properties || 0}</div>
            <div className="text-text-secondary">Properties</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card p-6">
            <MessageCircle className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{overview?.total_conversations || 0}</div>
            <div className="text-text-secondary">AI Conversations</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="glass-card p-6">
            <TrendingUp className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{overview?.total_leads || 0}</div>
            <div className="text-text-secondary">Total Leads</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="glass-card p-6">
            <Activity className="w-8 h-8 text-gold mb-4" />
            <div className="text-3xl font-bold mb-2">{dreamScores?.average_score || 0}</div>
            <div className="text-text-secondary">Avg Dream Score</div>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Onboarding Funnel */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="glass-card p-8">
            <h3 className="font-playfair text-2xl font-semibold mb-6">Onboarding Funnel</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={onboardingFunnelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" stroke="#A1A1AA" />
                <YAxis stroke="#A1A1AA" />
                <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #333' }} />
                <Bar dataKey="value" fill="#D4AF37" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 text-text-secondary text-sm">
              <p>Completion Rate: <span className="text-gold font-bold">{onboardingStats?.completion_rate}%</span></p>
              <p>Drop-off Rate: <span className="text-red-400 font-bold">{onboardingStats?.drop_off_rate}%</span></p>
            </div>
          </motion.div>

          {/* Dream Score Distribution */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="glass-card p-8">
            <h3 className="font-playfair text-2xl font-semibold mb-6">Dream Score Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={scoreDistributionData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {scoreDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #333' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 text-text-secondary text-sm">
              <p>Average Score: <span className="text-gold font-bold">{dreamScores?.average_score}</span></p>
              <p>Perfect Scores: <span className="text-emerald-400 font-bold">{dreamScores?.perfect_scores}</span></p>
            </div>
          </motion.div>
        </div>

        {/* AI Concierge Quality */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="glass-card p-8">
          <h3 className="font-playfair text-2xl font-semibold mb-6">AI Concierge Quality Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className="text-3xl font-bold text-gold mb-2">{conciergeQuality?.total_conversations || 0}</div>
              <div className="text-text-secondary text-sm">Total Conversations</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-2">{conciergeQuality?.unique_users || 0}</div>
              <div className="text-text-secondary text-sm">Unique Users</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-2">{conciergeQuality?.messages_per_user || 0}</div>
              <div className="text-text-secondary text-sm">Avg Messages/User</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gold mb-2">{conciergeQuality?.avg_response_length || 0}</div>
              <div className="text-text-secondary text-sm">Avg Response Length</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
